// deps
import React, { ReactNode, ReactElement, useContext, useMemo, memo } from "react";
import invariant from "tiny-invariant";

// internal deps
import { PaymentProgressLoader } from "@swiggy-private/payment-ui";
import { withStyles } from "@swiggy-private/core-ui";

// context
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

// utils
import { findNewVpaPaymentMethod, getPaymentHeaderSubtitleForSubRoutesV4 } from "@payment-client/helpers/PaymentHelper";
import { getItemCountAndAnnotation } from "@payment-client/helpers/CheckoutMetaHelper";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import { AddNewVpa } from "@swiggy-private/payment-ui";

// styles
import styles from "./styles.scss";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// hooks
import { useVerifyVpa } from "@payment-client/hooks/UseVerifyVpa";

type PaymentVerifyVpaProps = RouteProps<{ paymentMethodCode: string }>;

const PaymentVerfiyVpaWrapper = withStyles(function PaymentVerfiyVpaWrapper(props: {
    children: ReactNode;
}): ReactElement {
    const { checkoutMetaInfo, paymentResponse } = useContext(ShowPaymentContext);
    const { cartItemsCount, addressAnnotation } = useMemo(() => getItemCountAndAnnotation(checkoutMetaInfo), [
        checkoutMetaInfo,
    ]);
    const { amountToPay } = useContext(PaymentPayContext);

    const subtitleText = useMemo(() => {
        return getPaymentHeaderSubtitleForSubRoutesV4({
            selectedAddressAnnotation: addressAnnotation,
            cartAmount: String(amountToPay),
            itemCount: cartItemsCount,
            presentationDetails: paymentResponse?.presentationDetails,
        });
    }, [addressAnnotation, amountToPay, cartItemsCount, paymentResponse]);

    const header = <CommonHeaderContent title={"Add new UPI ID"} subtitle={subtitleText} grayscale />;

    return <CommonUIWrapper header={header} content={props.children} containerCSSClass={styles.container} />;
},
styles);

export const PaymentVerifyVpa = memo(function PaymentVerifyVpa({ data }: PaymentVerifyVpaProps): ReactElement | null {
    const { verifyVpa, verifyInProgress, invalidVpa } = useVerifyVpa();
    const { paymentResponse } = useContext(ShowPaymentContext);
    const actions = useMemo(() => ({ verifyVpa }), [verifyVpa]);

    const paymentMethodCode = data?.paymentMethodCode ?? null;

    if (!paymentMethodCode) {
        invariant(false, "Missing route data: paymentMethodCode");
    }

    if (!paymentResponse) {
        invariant(false, "Missing route data: paymentResponse");
    }

    const paymentMethod = findNewVpaPaymentMethod(paymentResponse, paymentMethodCode);
    invariant(paymentMethod.isSome(), "Payment method not found: " + paymentMethodCode);

    return (
        <>
            <PaymentVerfiyVpaWrapper>
                <AddNewVpa
                    actions={actions}
                    invalidVpa={invalidVpa}
                    verifyVpaInProgress={verifyInProgress}
                    methodData={paymentMethod.unwrap()}
                />
                {verifyInProgress && <PaymentProgressLoader />}
            </PaymentVerfiyVpaWrapper>
        </>
    );
});
